import {
  PolicySeverity,
  policySeveritySchema,
} from "@repo/api/schemas/policies.schema";
import {
  InsightsSummary,
  Ownership,
} from "@repo/api/schemas/policiesInventory.schema";
import { IconKind } from "@saviynt/design-system";

const Severity = policySeveritySchema.enum;

const ALERT_ICON_COLOR: Record<PolicySeverity, string> = {
  [Severity.critical]: "--color-foreground-critical-subtle",
  [Severity.high]: "--color-foreground-critical-subtle",
  [Severity.medium]: "--color-foreground-warning-subtle",
  [Severity.low]: "--color-foreground-success-medium",
};

export const getAlertIconColor = (highestRisk: PolicySeverity): string =>
  ALERT_ICON_COLOR[highestRisk] ?? "--color-foreground-secondary-medium";

export const getPoliciesIcon = (severity: PolicySeverity) => {
  switch (severity) {
    case Severity.critical:
    case Severity.high:
      return "Sad";
    case Severity.medium:
      return "Neutral";
    case Severity.low:
      return "Happy";
    default:
      return "" as IconKind;
  }
};

/**
The priority should always be:
Critical > High > Medium > Low
(even if low has more items than critical)
*/

export function getTopTwoRisks(insightsSummary: InsightsSummary) {
  const riskLevels = [
    {
      key: Severity.critical,
      label: "Critical",
      count: insightsSummary.criticalSeverityPolicyViolations,
      priority: 1,
    },
    {
      key: Severity.high,
      label: "High",
      count: insightsSummary.highSeverityPolicyViolations,
      priority: 2,
    },
    {
      key: Severity.medium,
      label: "Medium",
      count: insightsSummary.mediumSeverityPolicyViolations,
      priority: 3,
    },
    {
      key: Severity.low,
      label: "Low",
      count: insightsSummary.lowSeverityPolicyViolations,
      priority: 4,
    },
  ];

  return riskLevels
    .filter((r) => r.count > 0)
    .sort((a, b) => a.priority - b.priority) // sort by severity only
    .slice(0, 2);
}

export function sortOwners(owners: Ownership[]) {
  return owners.sort((a, b) => {
    // First, compare status
    if (a.status === b.status) {
      // If status is same, sort by name
      return a.name.localeCompare(b.name);
    }

    // If statuses are different, put 'Inactive' first
    return a.status.toLowerCase() === "inactive" ? -1 : 1;
  });
}

// Converts an array of JSON objects into a CSV formatted string.
type JsonRecord = Record<string, unknown>;

export const jsonToCSV = (data: JsonRecord[]): string => {
  if (!data || data.length === 0) return "";

  const delimiter = "\t";
  const headers = Object.keys(data[0]);

  const escapeValue = (value: unknown): string => {
    if (value === null || value === undefined) return "";

    const stringValue =
      typeof value === "object" ? JSON.stringify(value) : String(value);

    const escaped = stringValue.replace(/"/g, '""');
    return `"${escaped}"`;
  };

  const rows = data.map((row) =>
    headers.map((header) => escapeValue(row[header])).join(delimiter),
  );

  return [headers.map((h) => h.toUpperCase()).join(delimiter), ...rows].join(
    "\n",
  );
};

export function isEmpty(obj: unknown) {
  return (
    obj === null ||
    obj === undefined ||
    (typeof obj === "object" &&
      !Array.isArray(obj) &&
      Object.keys(obj).length === 0)
  );
}
